import type { TOCItem, TOCParent, TOC as TOCType } from '~/common/types';
import { digitizationLayerIds, digitizationTOC } from './digitizationLayers';
import { allSpecialDatasetLayerIds, datasetConfig } from './specialDatasets';
import { TOC as LEGACY_TOC } from './TOCLayers.data';

// Local guard copy (not imported from ./layers) to break the import cycle: otherwise the TOCLayers entry makes layers.ts build the store singleton before TOC is initialized.
function isTOCParent(item: TOCItem): item is TOCParent {
  return Boolean((item as TOCParent).children);
}

export const UtilityLineLayers: string[] = [
  'electricDistributionLines',
  'electricDistributionServiceLines',
  'electricTransmissionLines',
  'gasTransmission',
  'gasDistribution',
  'gasService',
  'sanitaryPipes',
  'waterTransmission',
  'waterMain',
  'waterService',
  'recycledWaterPipes',
  'stormPipes',
  'fiberRoutes',
  'submarineCables',
  'fossilFuelPipelines',
];

export const UtilityPointAndPolygonLayers: string[] = [
  'electricDistributionDevices',
  'electricDistributionStructures',
  'electricDistributionServiceVaults',
  'electricTransmissionStructures',
  'electricDistributionStreetLights',
  'electricDistributionOther',
  'gasPoints',
  'gasDistanceArrow',
  'pgeParcelBasemap',
  'sanitaryPoints',
  'waterPoints',
  'recycledWaterPoints',
  'stormPoints',
  'fiberLitBuildings',
  'cellTowers',
  'submarineLandings',
];

export const UtilityServiceLayers: string[] = ['electricDistributionServiceLines', 'gasService', 'waterService'];

export const AllUtilityLayers = [
  ...UtilityLineLayers,
  ...UtilityPointAndPolygonLayers,
  ...UtilityServiceLayers,
  ...digitizationLayerIds,
];

const StaticMetadataLayers = [...AllUtilityLayers, 'landUsePostmiles', 'landRightsParcels', 'landUseRailroads'];

// Special-dataset ids are read live because the org's DB bundle fills them after module load.
export const isSupportedMetadataLayer = (layerId: string) =>
  StaticMetadataLayers.includes(layerId) || allSpecialDatasetLayerIds.has(layerId);

export const AllTelcoLayers = [
  'fiberRoutes',
  'fiberLitBuildings',
  'cellTowers',
  'submarineCables',
  'submarineLandings',
];

export const ProjectConflictLayers: string[] = [
  // TEMP - COMMENT OUT PLANNED PROJECTS
  // 'projects',
  'pavingProjects',
  'cipProjects',
  'pavingMoratorium',
];

export const UtilitySectionLabels: Map<string, string> = new Map([
  ['Electric', 'Electric'],
  ['Gas', 'Gas'],
  ['Oil & Gas', 'Oil & Gas'],
  ['Water', 'Water'],
  ['Sewer', 'Sewer'],
  ['Storm', 'Storm'],
  ['Recycled Water', 'Recycled Water'],
  ['Telco', 'Telco'],
  ['Digitized Layers', 'Digitized Layers'],
]);

export const PhysicalInfrastructureLayers: string[] = ['landUseStateHighwayBridges', 'otherJurisdictionalDams'];

// Active TOC tree: seeded from legacy, refilled in place from the DB snapshot at bootstrap. Cloned here so TOCLayers.data stays pristine for parity.
export const TOC: TOCType = structuredClone(LEGACY_TOC);

const _idLabelMap: Map<string, string> = new Map();

// Parent labels for legend sections (parent + children can share id; separate map prevents "parent / layer name").
const _legendLabelMap: Map<string, string> = new Map();

export const TOCLabelMap = _idLabelMap;
export const TOCLegendParentMap = _legendLabelMap;

function rebuildLabelMaps(): void {
  _idLabelMap.clear();
  _legendLabelMap.clear();

  function traverseChildren(item: TOCItem, parent?: string) {
    if (isTOCParent(item)) {
      item.children.map((c) => traverseChildren(c, item.label));
    } else {
      if (allSpecialDatasetLayerIds.has(item.id)) {
        _legendLabelMap.set(item.id, `${parent} / ${item.label}`);
      } else if (parent && UtilitySectionLabels.has(parent)) {
        _legendLabelMap.set(item.id, `${parent} / ${item.label}`);
      }
      _idLabelMap.set(item.id, item.label);
    }
  }

  TOC.forEach((c) => {
    traverseChildren(c);
  });
  Object.values(datasetConfig).forEach(({ name, toc }) => {
    toc.forEach((c) => {
      traverseChildren(c, name);
    });
  });
}

// Initial build on app load, before any DB bundle is applied.
rebuildLabelMaps();

/**
 * Splice the `digitizationTOC` singleton into the DB tree's Utilities node.
 * The DB-delivered tree excludes the digitization branch (it drops the `sectionId`
 * those parents carry, so it can't round-trip byte-for-byte); the digitization
 * singleton supplies that branch instead. Pure — returns a new tree.
 */
export function composeActiveToc(dbToc: TOCType): TOCType {
  const next = structuredClone(dbToc);
  const utilities = next.find((n): n is TOCParent => isTOCParent(n) && n.label === 'Utilities');
  if (utilities) utilities.children.push(...structuredClone(digitizationTOC));
  return next;
}

/**
 * Merge `incoming` children into `existing`, reusing folders by exact label match and deduping
 * leaves by id. Pure; children of every merged folder are re-sorted alphabetically by label.
 */
function mergeChildren(existing: TOCItem[], incoming: TOCItem[]): TOCItem[] {
  const result: TOCItem[] = existing.map((n) => (isTOCParent(n) ? { ...n, children: [...n.children] } : { ...n }));

  for (const node of incoming) {
    if (isTOCParent(node)) {
      const match = result.find((r): r is TOCParent => isTOCParent(r) && r.label === node.label);
      if (match) {
        match.children = mergeChildren(match.children, node.children);
      } else {
        result.push({ ...node, children: mergeChildren([], node.children) });
      }
    } else {
      const dup = result.some((r) => !isTOCParent(r) && r.id === node.id);
      if (!dup) result.push({ ...node });
    }
  }

  return result.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Place the special-dataset "Custom Layers" node into `toc`, reusing an existing root by label if
 * present (merging children, deduping leaves by id) and pinning the single result to index 0.
 * If none exists, insert at the top. Pure — returns a new tree.
 */
export function mergeCustomLayersRoot(toc: TOCType, customLayers: TOCParent): TOCType {
  const idx = toc.findIndex((n): n is TOCParent => isTOCParent(n) && n.label === customLayers.label);
  if (idx === -1) {
    // Run children through the same dedup/sort as the merge path so we don't alias the caller's subtree.
    const inserted: TOCParent = {
      ...customLayers,
      children: mergeChildren([], customLayers.children),
    };
    return [inserted, ...toc];
  }

  const existing = toc[idx] as TOCParent;
  const merged: TOCParent = {
    ...existing,
    expanded: true,
    children: mergeChildren(existing.children, customLayers.children),
  };
  const rest = toc.filter((_, i) => i !== idx);
  return [merged, ...rest];
}

/**
 * Refill the active `TOC` + label maps from the DB snapshot's `toc` bundle. Must run before the
 * store's `setTOCLayersConfig` so the org-setup clone sees the DB tree.
 */
export function applyDbTocBundle(bundle: { TOC: TOCType }): void {
  const next = composeActiveToc(bundle.TOC);
  TOC.length = 0;
  TOC.push(...next);
  rebuildLabelMaps();
}

/**
 * Restore the active `TOC` (+ label maps) to the legacy tree. `TOC` is a mutable singleton, so the
 * no-delivery path must call this on every org switch or a previous org's DB tree leaks through.
 */
export function resetActiveTocToLegacy(): void {
  TOC.length = 0;
  TOC.push(...structuredClone(LEGACY_TOC));
  rebuildLabelMaps();
}
